import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Textarea, Select } from '@fluentui/react-components'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useQueryClient } from '@tanstack/react-query'
import { deriveAppTypesIdsFromAppTypesNumber, deriveAppTypesNumberFromAppTypesIds } from '@/util/appTypes'
import { AppTypesAssignmentComponent } from './AppTypesAssignmentComponent'
import { useCallback } from 'react'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsNewApplication = RestSchemas['Messages.Applications.NewApplication']
type MessagesApplicationsUpdateApplication = RestSchemas['Messages.Applications.UpdateApplication']

type AppTypes = RestSchemas['DataModellingPlatform.Common.AppTypes']

export type ApplicationForm = {
  appName: string
  appKey: string
  description: string
  status: string
  maxFileSizeMb: number
  fileTypes: string
  appTypesIdsArray: number[]
}

type AddApplicationFormProps = {
  onSubmit: () => void
  edit?: boolean
  selectedApp?: MessagesApplicationsApplication
}

export const AddApplicationForm = (props: AddApplicationFormProps) => {
  const queryClient = useQueryClient()

  const { postApplicationsApiUpdateApplication, putApplicationsApiAddApplication, getApplicationsApiGetApplications } =
    createRestClient()

  const invalidateQueriesOnSuccess = () => {
    getApplicationsApiGetApplications.invalidateQueries(queryClient)
  }

  const { mutate: updateApplication, isPending: isUpdating } = postApplicationsApiUpdateApplication.useMutation()
  const { mutate: addApplication, isPending: isAdding } = putApplicationsApiAddApplication.useMutation()

  const {
    register,
    formState: { errors },
    // setError,
    reset,
    handleSubmit,
    getValues,
    setValue,
    // control
  } = useForm<ApplicationForm>({
    defaultValues: props.selectedApp
      ? {
          appName: props.selectedApp.appName,
          appKey: props.selectedApp.appKey,
          description: props.selectedApp.description,
          status: props.selectedApp.status,
          maxFileSizeMb: props.selectedApp.maxFileSizeMb,
          fileTypes: props.selectedApp.fileTypes,
          appTypesIdsArray: deriveAppTypesIdsFromAppTypesNumber(props.selectedApp?.appTypes),
        }
      : {
          appName: '',
          appKey: '',
          description: '',
          status: 'Developing',
          maxFileSizeMb: 50,
          fileTypes: '*.csv/*.xlsx',
          appTypesIdsArray: [],
        },
  })

  const onSubmit = (data: ApplicationForm) => {
    if (props.edit) {
      const updatedApp: MessagesApplicationsUpdateApplication = {
        id: props.selectedApp!.applicationId!,
        name: data.appName,
        fileType: data.fileTypes,
        ...data,
        appTypes: deriveAppTypesNumberFromAppTypesIds(data.appTypesIdsArray),
      }

      updateApplication(
        { body: updatedApp },
        {
          onSuccess: () => {
            invalidateQueriesOnSuccess()
            props.onSubmit()
            toast.success('App successfully updated')
            reset()
          },
          onError: (_e) => {
            toast.error('Error in updating app')
          },
        }
      )
    } else {
      const newApp: MessagesApplicationsNewApplication = {
        name: data.appName,
        fileType: data.fileTypes,
        ...data,
        appTypes: deriveAppTypesNumberFromAppTypesIds(data.appTypesIdsArray) as AppTypes,
      }

      addApplication(
        { body: newApp },
        {
          onSuccess: () => {
            invalidateQueriesOnSuccess()
            props.onSubmit()
            toast.success('App successfully added')
            reset()
          },
          onError: (_e) => {
            toast.error('Error in adding app')
          },
        }
      )
    }
  }

  const handleAppTypesChange = useCallback(
    (updatedAppTypes: number[]) => {
      setValue('appTypesIdsArray', updatedAppTypes)
    },
    [setValue]
  )

  return (
    <div className="form-wrapper">
      <div>
        <Field
          label="Key"
          validationState={errors.appKey ? 'error' : 'none'}
          validationMessage={errors.appKey?.message}
        >
          <Input
            {...register('appKey', {
              required: 'Key is required',
              maxLength: 256,
            })}
          />
        </Field>
        <Field
          label="Name"
          required
          validationState={errors.appName ? 'error' : 'none'}
          validationMessage={errors.appName?.message}
        >
          <Input
            {...register('appName', {
              required: 'Name is required',
              maxLength: 256,
            })}
          />
        </Field>
        <Field
          label="Description"
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Textarea {...register('description', { maxLength: 256 })} rows={4} />
        </Field>
        <Field
          label="Status"
          validationState={errors.status ? 'error' : 'none'}
          validationMessage={errors.status?.message}
        >
          <Select
            //placeholder="Status"
            {...register('status', {
              required: 'Status is required',
              maxLength: 256,
            })}
          >
            <option key={'Developing'}>Developing</option>
            <option key={'Coming Soon'}>Coming Soon</option>
            <option key={'Live'}>Live</option>
          </Select>
        </Field>
        <Field
          label="Max File Size (MB)"
          validationState={errors.maxFileSizeMb ? 'error' : 'none'}
          validationMessage={errors.maxFileSizeMb?.message}
        >
          <Input {...register('maxFileSizeMb', {})} type="number" />
        </Field>

        <Field
          label="File Types"
          validationState={errors.fileTypes ? 'error' : 'none'}
          validationMessage={errors.fileTypes?.message}
        >
          <Input {...register('fileTypes', { maxLength: 128 })} />
        </Field>

        <AppTypesAssignmentComponent
          assignedAppTypesIds={getValues('appTypesIdsArray')}
          onAppTypesChange={handleAppTypesChange}
        />
      </div>
      <div className="form-action-container">
        <Button disabled={isUpdating || isAdding} text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
